import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

export const taskKeys = {
  list: ["tasks"] as const,
  summary: ["dashboard", "summary"] as const,
};

export function useDashboardSummary() {
  return useQuery({
    queryKey: taskKeys.summary,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/dashboard/summary", {});
      if (error || !data) throw new Error("Failed to load dashboard");
      return data;
    },
  });
}

export function useTasks() {
  return useQuery({
    queryKey: taskKeys.list,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/tasks", {
        params: { query: { status: "open" } },
      });
      if (error || !data) throw new Error("Failed to load tasks");
      return data;
    },
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: taskKeys.list });
  qc.invalidateQueries({ queryKey: taskKeys.summary });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await apiClient.POST("/api/v1/tasks/{task_id}/done", {
        params: { path: { task_id: taskId } },
      });
      if (error) throw new Error("Failed to complete task");
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDismissTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await apiClient.POST("/api/v1/tasks/{task_id}/dismiss", {
        params: { path: { task_id: taskId } },
      });
      if (error) throw new Error("Failed to dismiss task");
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useGenerateAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST("/api/v1/tasks/generate", {});
      if (error || !data) throw new Error("Failed to generate alerts");
      return data;
    },
    onSuccess: () => invalidateAll(qc),
  });
}
